import { useHttpClient } from '@repo/api-client/react';
import {
  DeregisterPushTokenResponseSchema,
  RegisterPushTokenRequestSchema,
  RegisterPushTokenResponseSchema,
  type DeregisterPushTokenResponse,
  type RegisterPushTokenRequest,
  type RegisterPushTokenResponse,
} from '@repo/api-client/dtos';
import { useMutation } from '@tanstack/react-query';

import { deleteJSON, postJSON } from '../internal/http';

/**
 * `POST /v1/users/me/push-tokens` — register an Expo push token
 * after a successful sign-in.
 *
 * The hook is intentionally bare: no cache reads (the token is
 * a per-device routing artefact, not user-facing data) and no
 * optimistic updates (the server upserts; our client never
 * needs to maintain a list of tokens). Callers fire-and-forget
 * the result.
 */
export function useRegisterPushToken() {
  const http = useHttpClient();

  const mutation = useMutation({
    mutationFn: async (
      input: RegisterPushTokenRequest,
    ): Promise<RegisterPushTokenResponse> => {
      const body = RegisterPushTokenRequestSchema.parse(input);
      return postJSON(
        http,
        '/users/me/push-tokens',
        body,
        RegisterPushTokenResponseSchema,
      );
    },
  });

  return {
    register: mutation.mutateAsync as (
      input: RegisterPushTokenRequest,
    ) => Promise<RegisterPushTokenResponse>,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * `DELETE /v1/users/me/push-tokens/:token` — deregister on
 * sign-out. The server returns `{ deleted: 0 | 1 }` so the
 * caller can warn-log a stale-token race without treating it
 * as an error.
 */
export function useDeregisterPushToken() {
  const http = useHttpClient();

  const mutation = useMutation({
    mutationFn: async (token: string): Promise<DeregisterPushTokenResponse> => {
      return deleteJSON(
        http,
        `/users/me/push-tokens/${encodeURIComponent(token)}`,
        DeregisterPushTokenResponseSchema,
      );
    },
  });

  return {
    deregister: mutation.mutateAsync as (
      token: string,
    ) => Promise<DeregisterPushTokenResponse>,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
